import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Sparkles, Heart, Target, Zap, Leaf, Diamond,
  CheckCircle2, ArrowRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';
import FloatingNav from './FloatingNav';
import '../styles/design-system.css';
import aguiasLivro from '../assets/aguias-livro.jpg';
import aguiasLancamento from '../assets/aguias-lancamento.jpg';

// ── Eagle SVG Logo ─────────────────────────────────────────────────────────────
const EagleLogo = ({ size = 48, color = 'var(--accent-gold-400)' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Body */}
    <ellipse cx="50" cy="58" rx="10" ry="14" fill={color} opacity="0.9"/>
    {/* Head */}
    <circle cx="50" cy="38" r="7" fill={color}/>
    {/* Beak */}
    <path d="M50 42 L55 46 L50 45 Z" fill={color} opacity="0.7"/>
    {/* Left wing */}
    <path d="M50 52 C35 44, 12 50, 5 42 C14 38, 32 46, 50 52 Z" fill={color} opacity="0.85"/>
    {/* Right wing */}
    <path d="M50 52 C65 44, 88 50, 95 42 C86 38, 68 46, 50 52 Z" fill={color} opacity="0.85"/>
    {/* Left wing tip detail */}
    <path d="M5 42 C8 36, 18 40, 28 46" stroke={color} strokeWidth="1" opacity="0.5" fill="none"/>
    {/* Right wing tip detail */}
    <path d="M95 42 C92 36, 82 40, 72 46" stroke={color} strokeWidth="1" opacity="0.5" fill="none"/>
    {/* Tail feathers */}
    <path d="M44 70 L40 82 L50 75 L60 82 L56 70 Z" fill={color} opacity="0.7"/>
    {/* Eye */}
    <circle cx="53" cy="36" r="1.5" fill="white"/>
  </svg>
);

const scrollToContact = (navigate) => {
  if (window.location.pathname === '/') {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  } else {
    navigate('/#contact');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  }
};

// ── Animation Variants ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

// ── Data ──────────────────────────────────────────────────────────────────────
const pillars = [
  { label: 'Espírito', color: 'var(--accent-purple-200)' },
  { label: 'Alma', color: 'var(--accent-rose-200)' },
  { label: 'Corpo', color: 'var(--accent-sage-200)' },
  { label: 'Estrutura', color: 'var(--accent-gold-200)' },
];

const howWeWork = [
  'Espiritualidade aplicada',
  'Cura emocional e fortalecimento interior',
  'Comportamento humano e inteligência emocional',
  'Posicionamento e identidade',
  'Educação financeira com princípios do Reino',
  'Direcionamento prático para vida e decisões',
];

const forWhom = [
  'Sentem travadas em seus resultados, mas sabem que nasceram para mais',
  'Desejam romper ciclos emocionais e limitações internas',
  'Buscam alinhamento espiritual e clareza de propósito',
  'Querem se posicionar com firmeza, constância e abundância',
  'Precisam organizar sua vida, inclusive na área financeira',
  'Decidiram não se conformar, mas se transformar',
];

const methodology = [
  { step: '01', title: 'Despertar', desc: 'Reconhecer sua identidade e realidade atual' },
  { step: '02', title: 'Conectar', desc: 'Alinhar mente, emoções e espírito ao propósito' },
  { step: '03', title: 'Transformar', desc: 'Romper padrões e assumir um novo posicionamento' },
  { step: '04', title: 'Estruturar', desc: 'Organizar a vida prática e financeira' },
  { step: '05', title: 'Prosperar', desc: 'Gerar resultados com consistência' },
];

const differentials = [
  'Psicoterapia cristã',
  'Neurociência e comportamento humano',
  'Formação de líderes',
  'Educação financeira com mais de 21 anos de experiência',
  'Espiritualidade prática e ministerial',
];

// ── Component ─────────────────────────────────────────────────────────────────
const ProjetoAguiasPage = () => {
  const { theme } = useLanguage();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const cardBg = isDark
    ? 'rgba(28,28,30,0.7)'
    : 'rgba(255,255,255,0.85)';
  const cardBorder = isDark
    ? 'rgba(255,255,255,0.06)'
    : 'rgba(0,0,0,0.06)';

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
      <Header />

      <main style={{ paddingTop: 80 }}>

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden"
          style={{
            background: isDark
              ? 'var(--gradient-hero-warm)'
              : 'var(--alternate-gradient-hero-warm)',
            minHeight: '70vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Decorative glow */}
          <div style={{
            position: 'absolute', top: '-10%', right: '-5%',
            width: 500, height: 500,
            background: 'radial-gradient(circle, rgba(184,134,11,0.1) 0%, transparent 70%)',
            filter: 'blur(60px)', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: '-10%', left: '-5%',
            width: 400, height: 400,
            background: 'radial-gradient(circle, rgba(184,134,11,0.06) 0%, transparent 70%)',
            filter: 'blur(60px)', pointerEvents: 'none',
          }} />

          <div className="container relative z-10 py-24">
            {/* Back link */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm"
                style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
              >
                <ArrowLeft size={16} />
                Voltar
              </Link>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="max-w-3xl"
            >
              {/* Badge */}
              <motion.div variants={fadeUp} className="mb-6">
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full"
                  style={{
                    background: 'rgba(184,134,11,0.08)',
                    border: '1px solid rgba(184,134,11,0.3)',
                  }}
                >
                  <span
                    className="mono-text"
                    style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}
                  >
                    PROJETO ÁGUIAS
                  </span>
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h1 variants={fadeUp} className="heading-hero mb-4">
                Despertar.{' '}
                <span className="gold-accent">Conectar.</span>{' '}
                Transformar.
              </motion.h1>

              {/* Sub */}
              <motion.p variants={fadeUp} className="body-large mb-10" style={{ maxWidth: 620 }}>
                Um movimento direcionado a mulheres que decidiram não viver no automático,
                não se conformar com limitações e não negociar seu propósito.
              </motion.p>

              {/* CTA */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToContact(navigate)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Quero participar
                  <ArrowRight size={16} />
                </button>
                <Link to="/#contact" className="btn-secondary inline-flex items-center gap-2">
                  Saiba mais
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Galeria de Fotos ───────────────────────────────────────────── */}
        <section
          className="py-20"
          style={{ background: isDark ? '#111' : '#0d0d0d', overflow: 'hidden' }}
        >
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5"
                style={{ background: 'rgba(184,134,11,0.15)', border: '1px solid rgba(184,134,11,0.4)' }}
              >
                <EagleLogo size={18} />
                <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                  O DESPERTAR DAS ÁGUIAS
                </span>
              </div>
              <h2 className="heading-1" style={{ color: '#fff' }}>
                O livro que é{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #d4a017, #f0c040, #b8860b)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>um movimento</span>
              </h2>
              <p className="body-medium mt-3" style={{ color: 'rgba(255,255,255,0.55)', maxWidth: 480, margin: '0.75rem auto 0' }}>
                A visão com propósito — por Lucimar Bizoni
              </p>
            </motion.div>

            {/* Photo Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem', maxWidth: 960, margin: '0 auto' }}>

              {/* Photo 1 — Lançamento */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ scale: 1.02 }}
                style={{
                  position: 'relative',
                  borderRadius: 20,
                  overflow: 'hidden',
                  aspectRatio: '4/5',
                  cursor: 'pointer',
                  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                }}
              >
                <img
                  src={aguiasLancamento}
                  alt="Lucimar Bizoni no lançamento do livro O Despertar das Águias"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center top',
                    display: 'block',
                  }}
                />
                {/* Gradient overlay */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                }} />
                {/* Caption */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '1.5rem 1.75rem',
                }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(184,134,11,0.2)',
                    border: '1px solid rgba(184,134,11,0.5)',
                    borderRadius: 99, padding: '3px 12px',
                    marginBottom: '0.5rem',
                  }}>
                    <EagleLogo size={12} />
                    <span style={{ color: 'var(--accent-gold-400)', fontSize: '0.62rem', letterSpacing: '0.1em', fontFamily: 'monospace' }}>LANÇAMENTO</span>
                  </div>
                  <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 600, margin: 0, lineHeight: 1.4 }}>
                    O Despertar das Águias
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', margin: '4px 0 0', lineHeight: 1.4 }}>
                    A visão com propósito
                  </p>
                </div>
              </motion.div>

              {/* Photo 2 — Livro */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ scale: 1.02 }}
                style={{
                  position: 'relative',
                  borderRadius: 20,
                  overflow: 'hidden',
                  aspectRatio: '4/5',
                  cursor: 'pointer',
                  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                }}
              >
                <img
                  src={aguiasLivro}
                  alt="Autógrafo no livro O Despertar das Águias"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center',
                    display: 'block',
                  }}
                />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.05) 45%, transparent 100%)',
                }} />
                {/* Caption */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  padding: '1.5rem 1.75rem',
                }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(184,134,11,0.2)',
                    border: '1px solid rgba(184,134,11,0.5)',
                    borderRadius: 99, padding: '3px 12px',
                    marginBottom: '0.5rem',
                  }}>
                    <EagleLogo size={12} />
                    <span style={{ color: 'var(--accent-gold-400)', fontSize: '0.62rem', letterSpacing: '0.1em', fontFamily: 'monospace' }}>O LIVRO</span>
                  </div>
                  <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 600, margin: 0, lineHeight: 1.4 }}>
                    Autógrafo da autora
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', margin: '4px 0 0', lineHeight: 1.4 }}>
                    Cada exemplar, uma história transformada
                  </p>
                </div>
              </motion.div>

            </div>

            {/* Bottom quote strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{
                textAlign: 'center', marginTop: '3rem',
                padding: '2rem',
                borderRadius: 16,
                background: 'rgba(184,134,11,0.06)',
                border: '1px solid rgba(184,134,11,0.2)',
                maxWidth: 600,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <EagleLogo size={36} />
              <p style={{
                color: 'rgba(255,255,255,0.8)',
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1.25rem',
                fontStyle: 'italic',
                lineHeight: 1.7,
                marginTop: '0.75rem',
              }}>
                "Águias não nadam com patos — elas voam acima das tempestades."
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── Essência + Pilares ────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-page)' : '#FFFFFF' }}>
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-16 items-center">

              {/* Text */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <motion.div variants={fadeUp} className="mb-6">
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                    style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
                  >
                    <Sparkles size={14} style={{ color: 'var(--accent-gold-400)' }} />
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                      NOSSA ESSÊNCIA
                    </span>
                  </div>
                  <h2 className="heading-1 mb-4">
                    Mulheres fortes,<br />curadas e posicionadas.
                  </h2>
                  <p className="body-medium" style={{ maxWidth: 480 }}>
                    Levantamos mulheres que vivem com equilíbrio integral — porque não existe
                    prosperidade completa sem alinhamento em todas as áreas da vida.
                  </p>
                </motion.div>

                {/* Mission quote */}
                <motion.div
                  variants={fadeUp}
                  className="relative p-8 rounded-2xl mt-8"
                  style={{
                    background: isDark ? '#1a1a1a' : 'var(--bg-page)',
                    border: '1px solid var(--accent-gold-400)',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 0, right: 0,
                    padding: '1rem', opacity: 0.08,
                  }}>
                    <Target size={80} style={{ color: 'var(--accent-gold-400)' }} />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div style={{ width: 4, height: 24, background: 'var(--accent-gold-400)', borderRadius: 99 }} />
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.7rem', letterSpacing: '0.12em' }}>
                      NOSSO PROPÓSITO
                    </span>
                  </div>
                  <p className="font-serif italic text-xl leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                    "Transformamos potencial em potência — com intencionalidade e fundamento."
                  </p>
                </motion.div>
              </motion.div>

              {/* Pillars */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <motion.p variants={fadeUp} className="mono-text mb-6" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                  PILARES DO PROJETO
                </motion.p>
                <div className="grid grid-cols-2 gap-4">
                  {pillars.map(({ label, color }, i) => (
                    <motion.div
                      key={label}
                      variants={fadeUp}
                      whileHover={{ y: -6 }}
                      className="p-6 rounded-2xl flex flex-col items-start"
                      style={{ background: color, border: '1px solid var(--border-primary)' }}
                    >
                      <span style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>
                        {['✨', '💛', '🌿', '💎'][i]}
                      </span>
                      <span className="heading-3" style={{ color: 'var(--text-primary)' }}>{label}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── Como Atuamos ─────────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-section)' : '#FAFAFA' }}>
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-center mb-16"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
              >
                <Leaf size={14} style={{ color: 'var(--accent-gold-400)' }} />
                <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                  COMO ATUAMOS
                </span>
              </div>
              <h2 className="heading-1 mb-4">Encontros, mentorias e<br />imersões integradas</h2>
              <p className="body-medium mx-auto" style={{ color: 'var(--text-secondary)', maxWidth: 520 }}>
                Não tratamos apenas do sentir — tratamos do viver e sustentar resultados alinhados com propósito.
              </p>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto"
            >
              {howWeWork.map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex items-start gap-3 p-5 rounded-xl"
                  style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <CheckCircle2
                    size={18}
                    style={{ color: 'var(--accent-gold-400)', flexShrink: 0, marginTop: 2 }}
                  />
                  <span className="body-small" style={{ color: 'var(--text-primary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {item}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Metodologia ──────────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-page)' : '#FFFFFF' }}>
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
              >
                <Zap size={14} style={{ color: 'var(--accent-gold-400)' }} />
                <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                  METODOLOGIA
                </span>
              </div>
              <h2 className="heading-1">Um processo de destrave<br />e posicionamento</h2>
            </motion.div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="max-w-3xl mx-auto space-y-4"
            >
              {methodology.map(({ step, title, desc }, i) => (
                <motion.div
                  key={step}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-6 p-6 rounded-2xl"
                  style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <div
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold"
                    style={{
                      background: 'rgba(184,134,11,0.1)',
                      color: 'var(--accent-gold-400)',
                      fontSize: '0.8rem',
                      fontFamily: 'SF Mono, monospace',
                    }}
                  >
                    {step}
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1">{title}</h3>
                    <p className="body-small">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Para Quem + Diferencial ───────────────────────────────────────── */}
        <section className="py-24" style={{ background: isDark ? 'var(--bg-section)' : '#FAFAFA' }}>
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-16">

              {/* Para quem */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
              >
                <motion.div variants={fadeUp}>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                    style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
                  >
                    <Heart size={14} style={{ color: 'var(--accent-gold-400)' }} />
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                      PARA QUEM É
                    </span>
                  </div>
                  <h2 className="heading-1 mb-8">Mulheres que...</h2>
                </motion.div>
                <div className="space-y-3">
                  {forWhom.map((text, i) => (
                    <motion.div
                      key={i}
                      variants={fadeUp}
                      className="flex items-start gap-3"
                    >
                      <div
                        style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: 'var(--accent-gold-400)',
                          flexShrink: 0, marginTop: 8,
                        }}
                      />
                      <p className="body-medium">{text}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Diferencial */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
              >
                <motion.div variants={fadeUp}>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
                    style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
                  >
                    <Diamond size={14} style={{ color: 'var(--accent-gold-400)' }} />
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.12em' }}>
                      NOSSO DIFERENCIAL
                    </span>
                  </div>
                  <h2 className="heading-1 mb-4">Conduzido por<br />Lucimar Bizoni</h2>
                  <p className="body-medium mb-8" style={{ maxWidth: 420 }}>
                    Uma integração única de formação e experiência que permite tratar
                    a raiz — não apenas os sintomas.
                  </p>
                </motion.div>
                <div className="space-y-3">
                  {differentials.map((item, i) => (
                    <motion.div
                      key={i}
                      variants={fadeUp}
                      className="flex items-center gap-3 p-4 rounded-xl"
                      style={{
                        background: cardBg,
                        border: `1px solid ${cardBorder}`,
                      }}
                    >
                      <div
                        style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: 'var(--accent-gold-400)',
                          flexShrink: 0,
                        }}
                      />
                      <span className="body-small" style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── Prospere Teaser ───────────────────────────────────────────────── */}
        <section
          className="py-20 relative overflow-hidden"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #0a0500 0%, #1c1000 60%, #0a0800 100%)'
              : 'linear-gradient(135deg, #fdf6e3 0%, #fff0c0 60%, #fdf6e3 100%)',
          }}
        >
          {/* Flame motif */}
          <div style={{
            position: 'absolute', right: '-4%', top: '50%', transform: 'translateY(-50%)',
            opacity: 0.07, pointerEvents: 'none',
          }}>
            <svg width="400" height="400" viewBox="0 0 100 100" fill="none">
              <path d="M50 10 C50 10, 72 30, 72 52 C72 66, 62 78, 50 82 C38 78, 28 66, 28 52 C28 30, 50 10, 50 10Z" fill="var(--accent-gold-400)" opacity="0.9"/>
              <path d="M50 36 C50 36, 62 48, 62 58 C62 66, 56.5 72, 50 74 C43.5 72, 38 66, 38 58 C38 48, 50 36, 50 36Z" fill="white" opacity="0.2"/>
            </svg>
          </div>
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600, height: 600,
            background: 'radial-gradient(circle, rgba(220,150,0,0.1) 0%, transparent 70%)',
            filter: 'blur(80px)', pointerEvents: 'none',
          }} />

          <div className="container relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">

              {/* Left: label + title */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <motion.div variants={fadeUp} className="mb-4">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full"
                    style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.2)' }}
                  >
                    <EagleLogo size={13} />
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.65rem', letterSpacing: '0.1em', fontFamily: 'monospace' }}>
                      UMA EXTENSÃO DO PROJETO ÁGUIAS
                    </span>
                  </div>
                </motion.div>

                <motion.div variants={fadeUp} className="mb-5">
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full"
                    style={{ background: 'rgba(184,134,11,0.12)', border: '1px solid rgba(184,134,11,0.4)' }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>🔥</span>
                    <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.14em' }}>
                      PROSPERE — NEGÓCIOS COM DEUS
                    </span>
                  </div>
                </motion.div>

                <motion.h2 variants={fadeUp} className="heading-1 mb-5">
                  Negócios que geram{' '}
                  <span className="gold-accent">legado.</span>
                </motion.h2>

                <motion.p variants={fadeUp} className="body-medium mb-4" style={{ maxWidth: 460, color: 'var(--text-secondary)' }}>
                  Para empreendedores, empresários e líderes que compreenderam que
                  resultado sem propósito é vazio — e propósito sem ação não prospera.
                </motion.p>

                <motion.p variants={fadeUp} className="body-medium mb-8" style={{ maxWidth: 460, fontStyle: 'italic', color: 'var(--text-muted)' }}>
                  "O conformismo limita. A transformação posiciona."
                </motion.p>

                <motion.div variants={fadeUp}>
                  <Link
                    to="/projeto-prospere"
                    className="btn-primary inline-flex items-center gap-2"
                    style={{ textDecoration: 'none' }}
                  >
                    Conheça o Prospere
                    <ArrowRight size={16} />
                  </Link>
                </motion.div>
              </motion.div>

              {/* Right: pillars preview */}
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className="grid grid-cols-2 gap-3"
              >
                {[
                  { icon: '✝️', label: 'Fé aplicada' },
                  { icon: '💰', label: 'Educação Financeira' },
                  { icon: '🧠', label: 'Inteligência emocional' },
                  { icon: '🎯', label: 'Visão estratégica' },
                  { icon: '🔬', label: 'Comportamento humano' },
                  { icon: '🌱', label: 'Espiritualidade prática' },
                ].map(({ icon, label }, i) => (
                  <motion.div
                    key={label}
                    variants={fadeUp}
                    whileHover={{ y: -4 }}
                    className="p-4 rounded-xl flex items-center gap-3"
                    style={{
                      background: cardBg,
                      border: `1px solid ${cardBorder}`,
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>
                    <span className="body-small" style={{ color: 'var(--text-primary)', fontSize: '0.85rem', lineHeight: 1.4 }}>{label}</span>
                  </motion.div>
                ))}
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── CTA Final ────────────────────────────────────────────────────── */}
        <section
          className="py-24 relative overflow-hidden"
          style={{
            background: isDark
              ? 'var(--alternate-gradient-hero-warm)'
              : 'var(--gradient-hero-warm)',
          }}
        >
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600, height: 600,
            background: 'radial-gradient(circle, rgba(184,134,11,0.12) 0%, transparent 70%)',
            filter: 'blur(60px)', pointerEvents: 'none',
          }} />

          <div className="container relative z-10 text-center">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="max-w-2xl mx-auto"
            >
              <motion.span variants={fadeUp} style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <EagleLogo size={64} />
              </motion.span>

              <motion.h2 variants={fadeUp} className="heading-hero mb-6">
                Você foi chamada<br />para viver{' '}
                <span className="gold-accent">posicionada.</span>
              </motion.h2>

              <motion.p variants={fadeUp} className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
                Não se conforme — transforme-se.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3 mb-10 text-sm" style={{ color: 'var(--text-muted)' }}>
                {['Desperte.', 'Conecte-se.', 'Destrave-se.'].map((w) => (
                  <span key={w} style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '1.1rem', color: 'var(--text-secondary)' }}>
                    {w}
                  </span>
                ))}
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => scrollToContact(navigate)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Quero me transformar
                  <ArrowRight size={16} />
                </button>
                <Link to="/" className="btn-secondary inline-flex items-center gap-2">
                  <ArrowLeft size={16} />
                  Voltar ao início
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </main>

      <Footer />
      <FloatingWhatsApp />
      <FloatingNav />
    </div>
  );
};

export default ProjetoAguiasPage;
